/**
 * "Was this helpful?" at the foot of an article, and the way out when it wasn't.
 *
 * The vote is stored as well as tracked: kept only in analytics it sat away from
 * the page it was about, so the editing tree now shows the counts next to the
 * page they belong to. The totals are deliberately not shown to readers — a
 * visible score invites the gaming that makes it useless.
 *
 * A no is followed by the existing feedback modal, because the sentence someone
 * types there is worth more than the thumb. Contact us stays visible either way:
 * an answer that did not help is exactly when someone still has a question.
 */

import { Typography } from "@/components/common/Typography/Typography"
import { FeedbackSubmissionModal } from "@/components/feature/Modals/FeedbackSubmissionModal"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { URLS } from "@/utils/urls.const"
import { IconMail, IconThumbDown, IconThumbUp } from "@tabler/icons-react"
import { Check } from "lucide-react"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import { useState } from "react"
import { useSubmitArticleFeedback } from "../api/helpContentApi"
import { SWAP, TRAVEL } from "../motion"

export function ArticleFeedback({
  articleId,
  categoryId,
  lang,
}: {
  articleId: string
  categoryId: string
  lang: string
}) {
  const { translate } = useTranslation()
  const [vote, setVote] = useState<"up" | "down" | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const submit = useSubmitArticleFeedback(articleId, lang)

  const handleVote = (value: "up" | "down") => {
    setVote(value)
    trackEvent(AnalyticsEvent.HELP_ARTICLE_RATED, {
      article_id: articleId,
      category_id: categoryId,
      lang,
      helpful: value === "up",
    })
    // A failed vote is not worth interrupting a reader over; the thanks stands
    // either way and the analytics event already went.
    submit.mutate(value === "up")
  }

  return (
    <div className="mt-XL flex flex-wrap items-center gap-M border-t border-border pt-L">
      <AnimatePresence mode="wait" initial={false}>
        <m.div
          key={vote === null ? "question" : "thanks"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={SWAP}
          className="flex flex-wrap items-center gap-M"
        >
          {vote === null ? (
            <>
              <Typography as="span" variant="textXS" className="text-grey5">
                {translate("HELPDESK/HELPFUL_QUESTION")}
              </Typography>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleVote("up")}
                >
                  <IconThumbUp size={14} aria-hidden="true" />
                  {translate("HELPDESK/HELPFUL_YES")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleVote("down")}
                >
                  <IconThumbDown size={14} aria-hidden="true" />
                  {translate("HELPDESK/HELPFUL_NO")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <m.span
                  aria-hidden="true"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={TRAVEL}
                  className="flex size-5 items-center justify-center rounded-full bg-primary/15 text-primary"
                >
                  <Check className="size-3" />
                </m.span>
                <Typography as="span" variant="textXS" className="text-grey5">
                  {translate("HELPDESK/HELPFUL_THANKS")}
                </Typography>
              </span>
              {vote === "down" ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setFeedbackOpen(true)}
                >
                  {translate("HELPDESK/HELPFUL_TELL_US")}
                </Button>
              ) : null}
            </>
          )}
        </m.div>
      </AnimatePresence>

      <div className="ml-auto flex items-center gap-2">
        <Typography as="span" variant="textXS" className="text-grey5">
          {translate("HELPDESK/STILL_STUCK")}
        </Typography>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="text-grey5 hover:text-white"
        >
          <a href={URLS.INFO_BOX.HELP}>
            <IconMail size={14} aria-hidden="true" />
            {translate("HELPDESK/CONTACT_US")}
          </a>
        </Button>
      </div>

      <FeedbackSubmissionModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
      />
    </div>
  )
}
